import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCrumsHeader from "../BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer as MuiDrawer,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import Checkbox from "../../common/Checkbox";
import Status from "../Status";
import Delete from "../Delete";
// import Edit from "../Edit";
import Pagination from "../TablePagination";
import CreateNewButton from "../CreateNewButton";
import { ReactComponent as CheckedWhiteIcon } from "../../../Assets/Icons/checkedWhiteBox.svg";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedWhiteBox.svg";
import { ReactComponent as UncheckedGreyIcon } from "../../../Assets/Icons/unCheckedGreyIcon.svg";
import blueCross from "../../../Assets/Icons/blueCross.svg";
import whiteTick from "../../../Assets/Icons/White Tick.svg";
import { ReactComponent as Image } from "../../../Assets/Icons/Image.svg";
import { ReactComponent as LocationIcon } from "../../../Assets/Icons/Location.svg";
import { ReactComponent as ApprovedSubCategoryIcon } from "../../../Assets/Icons/approvedsubCategoryIcon.svg";
import { ReactComponent as SubCategoryIcon } from "../../../Assets/Icons/subCategoryIcon.svg";
import { ReactComponent as UnapprovedSubCatIcon } from "../../../Assets/Icons/unapprovedSubCatIcon.svg";
import { ReactComponent as ApprovedRulesIcon } from "../../../Assets/Icons/approvedRulesIcon.svg";
import { ReactComponent as RulesIcon } from "../../../Assets/Icons/rulesIcon.svg";
import { ReactComponent as UnapprovedRulesIcon } from "../../../Assets/Icons/unapprovedRulesIcon.svg";
import descriptionIcon from "../../../Assets/Icons/descriptionIcon.svg";
import {
  deleteOffer,
  getNonDraftedOffers,
  getOffers,
  getOfferById,
  getOffersByMerchant,
  toggleOfferStatus,
  approveOrRejectResources,
} from "../../../services/offerService";
import ImagePopup from "../ImagePopup";
import LocationPopup from "../LocationPopup";
import CancelPopup from "../CancelPopup";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import debounce from "lodash.debounce";
import {
  separateDateTimeAndConvertToLocale,
} from "../../../utils/utilFunctions";
import { getS3PublicUrl } from "../../../utils/s3";
import Loader from "../../common/Loader";
import DetailsPopup from "../DetailsPopup";
import SubCategoryPopup from "../SubCategoryPopup";
import Confirmation from "./Confirmation";
import { DatePicker } from "antd";
import FilterButton from "../../common/FilterButton";
import infoIcon from "../../../Assets/Icons/infoIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import SearchBar from "../SearchBar";
import { Dropdown } from "antd";
import FilterPanel from "../FilterPanel";
import { getCategories } from "../../../services/offerService";
import { getMerchants } from "../../../services/merchantService";
import DetailsModal from "../DetailsModal";
import TableEmptyState from "../TableEmptyState";
import PopupContent from "./PopupContent";
import ActiveFilterBar from "../../common/ActiveFilterBar";
import "./offerList.scss";
import { formatCustomDateRangeLabel } from "../../../utils/filterLabelUtils";
import TruncatedTableCell from "../../common/TruncatedTableCell";

const MerchantSearchBox = ({ selectedMerchantId, handleSelectionChange, open }) => {
  const dispatch = useDispatch();
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [merchantSearchLoad, setMerchantSearchLoad] = useState(false);
  const [inputValue, setInputValue] = useState("");
  // Local state to safeguard the selected option object so the chip doesn't vanish
  const [selectedOption, setSelectedOption] = useState(null);

  const getMerchantDisplayLabel = (merchant) =>
    merchant?.businessName ||
    merchant?.business_name ||
    merchant?.merchantName ||
    merchant?.name ||
    "Unknown Merchant";

  const normalizeMerchantSearchValue = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const getMerchantSearchValues = (merchant) => [
    merchant?.businessName,
    merchant?.business_name,
  ]
    .filter(Boolean)
    .map((value) => normalizeMerchantSearchValue(value));

  const matchesMerchantSearch = (merchant, searchValue = "") => {
    const needle = normalizeMerchantSearchValue(searchValue);
    if (!needle) return true;

    const candidates = getMerchantSearchValues(merchant);

    return candidates.some((candidate) => candidate && candidate.includes(needle));
  };

  const fetchMerchants = useCallback(async (searchValue = "") => {
    setMerchantSearchLoad(true);
    try {
      const response = await dispatch(
        getMerchants({
          limit: 100,
          search: searchValue || undefined,
        })
      ).unwrap();
      if (response?.success) {
        setMerchantOptions(response?.data?.merchants || response?.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setMerchantSearchLoad(false);
    }
  }, [dispatch]);

  const debouncedFetchMerchants = useMemo(
    () => debounce((value) => fetchMerchants(value), 350),
    [fetchMerchants]
  );

  useEffect(() => {
    if (open) {
      fetchMerchants("");
      setInputValue("");
    }
    return () => {
      debouncedFetchMerchants.cancel();
    };
  }, [open, fetchMerchants, debouncedFetchMerchants]);

  useEffect(() => {
    if (selectedMerchantId) {
      const opt = merchantOptions.find(m => m._id === selectedMerchantId);
      if (opt && (!selectedOption || selectedOption._id !== selectedMerchantId)) {
        setSelectedOption(opt);
      }
    } else {
      setSelectedOption(null);
    }
  }, [merchantOptions, selectedMerchantId, selectedOption]);

  const filterMerchantResults = (options) => {
    const needle = normalizeMerchantSearchValue(inputValue);
    const seenIds = new Set();
    const score = (name) => {
      if (!needle) return 2;
      if (name === needle) return 0;
      if (name.startsWith(needle)) return 1;
      if (name.includes(needle)) return 2;
      return 3;
    };

    const deduped = options.filter((option) => {
      const optionId = option?._id || option?.id || getMerchantDisplayLabel(option);
      if (seenIds.has(optionId)) return false;
      seenIds.add(optionId);
      return true;
    });

    return deduped
      .filter((option) => {
        const name = normalizeMerchantSearchValue(getMerchantDisplayLabel(option));
        return matchesMerchantSearch(option, needle) && (!needle || name.includes(needle));
      })
      .sort((a, b) => {
        const nameA = normalizeMerchantSearchValue(getMerchantDisplayLabel(a));
        const nameB = normalizeMerchantSearchValue(getMerchantDisplayLabel(b));
        const diff = score(nameA) - score(nameB);
        if (diff !== 0) return diff;
        return nameA.localeCompare(nameB);
      });
  };

  return (
    <Autocomplete
      multiple
      limitTags={1}
      options={merchantOptions}
      getOptionLabel={(option) => getMerchantDisplayLabel(option)}
      loading={merchantSearchLoad}
      value={selectedOption ? [selectedOption] : []}
      inputValue={inputValue}
      disableClearable={true}
      onChange={(_, newValue) => {
        const selectedOpt = newValue.length > 0 ? newValue[newValue.length - 1] : null;
        setSelectedOption(selectedOpt || null);
        handleSelectionChange("merchantId", selectedOpt ? selectedOpt._id : null, selectedOpt);
        setInputValue("");
        if (!selectedOpt) fetchMerchants(""); // Refetch list when cleared
      }}
      onInputChange={(_, value, reason) => {
        // Prevent MUI from blanking input when users just click away or dropdown closes 
        if (reason !== "reset") {
          setInputValue(value);
          debouncedFetchMerchants(value);
        }
      }}
      autoComplete
      autoHighlight
      includeInputInList
      filterSelectedOptions
      filterOptions={filterMerchantResults}
      noOptionsText="No merchants found"
      forcePopupIcon={true}
      popupIcon={
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A0AEC0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={index}
            label={getMerchantDisplayLabel(option)}
            onDelete={() => {
              setSelectedOption(null);
              handleSelectionChange("merchantId", null, null);
              setInputValue("");
              fetchMerchants(""); // Refetch list when cleared
            }}
            sx={{
              backgroundColor: '#1EBBF0',
              color: 'white',
              borderRadius: '16px',
              height: '28px',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'none',
              paddingLeft: '4px',
              letterSpacing: '0.5px',
              '& .MuiChip-deleteIcon': {
                color: 'white',
                opacity: 0.8,
                fontSize: '14px',
                marginRight: '6px',
                '&:hover': {
                  opacity: 1,
                  color: 'white'
                }
              }
            }}
          />
        ))
      }
      isOptionEqualToValue={(option, value) => option?._id === value?._id}
      sx={{
        '& .MuiAutocomplete-popupIndicatorOpen': {
          transform: 'none',
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedOption ? "" : "Search"}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            className: "min-h-[40px] !rounded-[8px] !text-[14px]",
            style: { padding: '4px 8px', paddingRight: '12px' },
            endAdornment: (
              <>
                {merchantSearchLoad && (
                  <div className="mr-2 flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-[#00ADEF] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {!merchantSearchLoad && params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            readOnly: !!selectedOption,
          }}
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#CED4DA',
              borderWidth: '1px'
            },
            '& input': {
              caretColor: selectedOption ? 'transparent' : 'auto',
              cursor: selectedOption ? 'default' : 'text',
            }
          }}
          className="mt-2 w-full"
        />
      )}
      className="w-full mt-2"
    />
  );
};

const AdminOfferlist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [clickedOffer, setClickedOffer] = useState(null);
  const [reason, setReason] = useState("");
  const [infoClicked, setInfoClicked] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);
  const [statusToggled, setStatusToggled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showSingleApprovalConfirm, setShowSingleApprovalConfirm] =
    useState(false);
  const [singleApprovalOfferId, setSingleApprovalOfferId] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const statusMutationLockRef = useRef(false);
  const [searching, setSearching] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fullOffersList, setFullOffersList] = useState([]);
  const [displayedOffers, setDisplayedOffers] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [popupOpenMode, setPopupOpenMode] = useState(null);

  // Persist filter selections across refreshes


  // Persist filter selections across refreshes
  const STORAGE_KEY = "ADMIN_OFFERS_LIST_FILTERS_V2";

  const getInitialFilters = () => {
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        const params = saved.filterParams || null;
        // Migration: If duration was "DAY" (Today), clear it to null (All) by default
        if (params && params.duration === "DAY") {
          params.duration = null;
        }
        if (params && (params.status === "disabled" || params.status === "declined")) {
          params.status = "rejected";
        }
        return params;
      }
    } catch (_) { }
    return null;
  };

  const getInitialSelections = () => {
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        const selections = saved.selections || null;
        // Migration: If duration was "Today", clear it to "null" (All) by default
        if (selections && selections.duration === "Today") {
          selections.duration = null;
        }
        if (selections && (selections.status === "Declined" || selections.status === "Disabled")) {
          selections.status = "Rejected";
        }
        return selections;
      }
    } catch (_) { }
    return null;
  };

  const initialFilters = getInitialFilters();
  const [customDate, setCustomDate] = useState(() => {
    if (initialFilters?.startDate || initialFilters?.endDate) {
      return [
        initialFilters.startDate ? dayjs(initialFilters.startDate) : null,
        initialFilters.endDate ? dayjs(initialFilters.endDate) : null
      ];
    }
    return [null, null];
  });

  const [filterParams, setFilterParams] = useState(initialFilters || {
    category: null,
    subCategory: [], // Initialize as empty array for multi-select
    status: null,
    duration: null,
    offerType: null,
    redemptionMethod: null,
    creatorRole: null,
    merchantId: null,
  });

  const [searchInput, setSearchInput] = useState("");

  const clearOfferFilterState = () => {
    const initialState = createInitialState(filterDataWithMerchant);
    setSelections(initialState);
    setFilterParams({
      category: null,
      subcategory: [],
      status: null,
      duration: null,
      offerType: null,
      redemptionMethod: null,
      creatorRole: null,
      merchantId: null,
    });
    setCustomDate([null, null]);
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
    setSelected([]);
    setSelectedMerchantIds([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) { }
  };

  // Match other lists: load supporting data on mount, but do it silently
  // so the global fullscreen loader is driven by the main list fetch.
  useEffect(() => {
    getCategoriesData(true);
  }, []);


  // Add these mappings
  const DURATION_MAP = {
    "Today": "DAY",
    "This Week": "WEEK",
    "This Month": "MONTH",
    "This Year": "YEAR",
    "Custom Date": "CUSTOM",
  };

  const STATUS_MAP = {
    Live: "approved",
    Rejected: "rejected",
    Disabled: "pause",
    Pending: "pending",
    Drafted: "drafted",
    "In-Progress": "in-process",
    Deleted: "deleted",
  };

  const OFFER_TYPE_MAP = {
    "Instore Swoodle Offer": "INSTORE_SWOODLE_OFFER", // Corrected as per your API
    Online: "ONLINE_OFFER", // Corrected as per your API
  };

  const REDEMPTION_METHOD_MAP = {
    "Swoodle Code Offer": "SWOODLE_CODE_OFFER", // Based on common redemption types matching your API's IN_STORE
    "QR Code Offer": "QR_CODE", // Assuming QR/Bar Code are in-store methods
    "Bar Code Offer": "BAR_CODE", // Assuming QR/Bar Code are in-store methods
  };

  // Helper function to filter approved subcategories
  const getApprovedSubcategories = (category) => {
    if (
      !category ||
      !category.subCategory ||
      !Array.isArray(category.subCategory)
    ) {
      return [];
    }

    return category.subCategory;
  };

  // Helper function to update subcategories when a category is selected
  const updateSubcategoriesForCategory = (selectedCategory) => {
    if (!selectedCategory) return;

    // Get approved subcategories
    const approvedSubcategories = getApprovedSubcategories(selectedCategory);

    // Reset subcategory selections
    setSelections((prev) => ({
      ...prev,
      subcategory: [], // Initialize as empty array for multi-select
    }));
  };

  // Update the getCategoriesData function to populate filterData
  const getCategoriesData = async (silent = false) => {
    const categoryPromise = await dispatch(getCategories({ silent }))?.unwrap();
    if (categoryPromise?.success === true) {
      const categoriesData = categoryPromise?.data;
      setCategories(categoriesData);

      // Update selections with the first category as default if available
      if (categoriesData.length > 0) {
        // Also update subcategories based on the first category
        updateSubcategoriesForCategory(categoriesData[0]);
      }
    }
  };
  // Initialize selections with the initial state OR rehydrated state
  const [selections, setSelections] = useState(getInitialSelections() || {
    duration: null,
    status: null,
    category: null,
    subcategory: [],
    offerType: null,
    redemptionMethod: null,
    creatorRole: null,
    merchantId: null,
  });

  const subcategoryOptions = useMemo(() => {
    const selectedCategory = categories.find((cat) => cat._id === selections.category);
    return getApprovedSubcategories(selectedCategory);
  }, [categories, selections.category]);

  const activeCount = useMemo(() => {
    let count = 0;
    Object.keys(selections).forEach(key => {
      const val = selections[key];
      if (val !== null && val !== "" && !(Array.isArray(val) && val.length === 0)) {
        if (key === "subcategory" && Array.isArray(val)) {
          count += val.length;
        } else if (key !== "merchantName") { // Don't count name as a separate filter
          count += 1;
        }
      }
    });
    return count;
  }, [selections]);



  const filterData = useMemo(() => [
    {
      id: "duration",
      title: "Time Period",
      options: ["Today", "This Week", "This Month", "This Year", "Custom Date"],
      defaultValue: null,
      multiSelect: false,
      valueMap: DURATION_MAP,
      customContent: (selected, filterParams) => {
        console.log("Custom Date selected value:", selected);
        return selected === "Custom Date" ? (
          <div className="mt-3 flex flex-col gap-2" style={{ pointerEvents: 'auto', zIndex: 10000 }}>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-medium text-gray-600">Start Date</label>
              <DatePicker
                value={customDate?.[0]}
                onChange={(date) => {
                  console.log("Start date changed:", date);
                  const newDates = [date, customDate?.[1]];
                  setCustomDate(newDates);
                }}
                onClick={(e) => {
                  console.log("DatePicker clicked!", e);
                }}
                style={{ width: '100%', pointerEvents: 'auto' }}
                className="custom-date-picker"
                placeholder="Select Start Date"
                format="YYYY-MM-DD"
                showTime={false}
                allowClear={true}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="date-picker-popup"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-medium text-gray-600">End Date</label>
              <DatePicker
                value={customDate?.[1]}
                onChange={(date) => {
                  console.log("End date changed:", date);
                  const newDates = [customDate?.[0], date];
                  setCustomDate(newDates);
                }}
                onClick={(e) => {
                  console.log("DatePicker clicked!", e);
                }}
                style={{ width: '100%', pointerEvents: 'auto' }}
                className="custom-date-picker"
                placeholder="Select End Date"
                format="YYYY-MM-DD"
                showTime={false}
                allowClear={true}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="date-picker-popup"
              />
            </div>
          </div>
        ) : null;
      }
    },
    {
      id: "status",
      title: "Status",
      options: ["Live", "Rejected", "Disabled", "Pending", "Drafted", "Deleted"],
      multiSelect: false,
      valueMap: STATUS_MAP,
    },
    {
      id: "category",
      title: "Category",
      options: categories,
      defaultValue: "",
      multiSelect: false,
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "subcategory",
      title: "Subcategory",
      options: subcategoryOptions,
      defaultValue: [],
      multiSelect: true,
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "offerType",
      title: "Offer Type",
      options: ["Instore Swoodle Offer", "Online"],
      multiSelect: false,
      valueMap: OFFER_TYPE_MAP,
    },
    {
      id: "redemptionMethod",
      title: "Redemption Method",
      options: ["Swoodle Code Offer", "QR Code Offer", "Bar Code Offer"],
      multiSelect: false,
      valueMap: REDEMPTION_METHOD_MAP,
    },
    {
      id: "creatorRole",
      title: "Created By",
      options: ["Admin", "Merchant"],
      multiSelect: false,
      valueMap: {
        Admin: "admin",
        Merchant: "merchant",
      },
    },
  ], [categories, subcategoryOptions, customDate, selections.duration]);
  
  // Update the handleSelectionChange function
  const handleSelectionChange = (filterId, value, extraInfo = null) => {
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
      ...(filterId === "merchantId"
        ? {
            merchantName:
              extraInfo?.businessName ||
              extraInfo?.business_name ||
              extraInfo?.merchantName ||
              extraInfo?.name ||
              extraInfo?.user?.name ||
              null,
          }
        : {}),
    }));
  
    // If category changes, update subcategories
    if (filterId === "category") {
      // Find the selected category
      const selectedCategory = categories.find((cat) => cat._id === value);
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  };
  
  const filterDataWithMerchant = useMemo(() => {
    const list = [...filterData];

    // We want the order: Status, Search by Merchant, Time Period.
    // Let's swap Status and Time Period (duration) if they are in the default positions.
    const durationIdx = list.findIndex(f => f.id === "duration");
    const statusIdx = list.findIndex(f => f.id === "status");
    if (durationIdx !== -1 && statusIdx !== -1) {
      const statusObj = list[statusIdx];
      list[statusIdx] = list[durationIdx];
      list[durationIdx] = statusObj;
    }

    // Now find the new status index (should be first)
    const newStatusIdx = list.findIndex(f => f.id === "status");
    list.splice(newStatusIdx !== -1 ? newStatusIdx + 1 : 1, 0, {
      id: "merchantId",
      title: "Search by Merchant",
      customContent: (
        <MerchantSearchBox
          selectedMerchantId={selections.merchantId}
          handleSelectionChange={handleSelectionChange}
          open={open}
        />
      ),
    });
    return list;
  }, [filterData, selections.merchantId, selections.merchantName, open]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    Object.entries(filterParams).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;
      if (key === "merchantName" || key === "startDate" || key === "endDate") return;

      const filter = filterDataWithMerchant.find(f => f.id === key);
      if (!filter) return;

      const values = Array.isArray(value) ? value : [value];
      values.forEach(val => {
        let displayLabel = val;
        if (filter.optionValueKey) {
          const opt = filter.options.find(o => o[filter.optionValueKey] === val);
          displayLabel = opt ? opt[filter.optionLabelKey] : null;
        } else if (filter.valueMap) {
          displayLabel = Object.keys(filter.valueMap).find(k => filter.valueMap[k] === val) || null;
        } else if (key === "merchantId") {
          displayLabel = filterParams.merchantName || selections.merchantName || "Merchant";
        }

        if (displayLabel) {
          chips.push({ id: key, value: val, label: displayLabel });
        }
      });
    });

    // Special handling for custom date range label
    if (filterParams.duration === "CUSTOM") {
      const dChip = chips.find(c => c.id === 'duration');
      if (dChip) {
        dChip.label = formatCustomDateRangeLabel(
          filterParams.startDate || customDate?.[0],
          filterParams.endDate || customDate?.[1]
        );
      }
    }

    return chips;
  }, [filterParams, filterDataWithMerchant, customDate]);
  const isOfferCustomDateIncomplete =
    selections?.duration === "Custom Date" &&
    (!customDate?.[0] || !customDate?.[1]);

  // Create initial state logic (helper function)
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // No longer need immediate rehydration in useEffect as we do it in initializers
  // which prevents the double API call on mount.
  useEffect(() => {
    // Only if somehow it's empty, we could fall back, but initializers are better.
  }, []);

  // Save filters whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ selections, filterParams })
      );
    } catch (_) { }
  }, [selections, filterParams]);

  useEffect(() => {
    const hasDeepLinkState = location?.state?.openOfferId || location?.state?.offerId;
    if (!location.search && !hasDeepLinkState) {
      clearOfferFilterState();
    }
  }, [location.pathname, location.search, location?.state?.openOfferId, location?.state?.offerId]);

  // Use effect to initialize subcategories when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && selections.category) {
      const selectedCategory = categories.find(
        (cat) => cat._id === selections.category
      );
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  }, [categories, selections.category]);



  // Update the handleReset function
  const handleReset = () => {
    const initialState = createInitialState(filterDataWithMerchant);
    setSelections({ ...initialState, merchantName: null });
    if (categories.length > 0) {
      updateSubcategoriesForCategory(categories[0]);
    }
    setFilterParams({
      category: null,
      subcategory: [],
      status: null,
      duration: null,
      offerType: null,
      redemptionMethod: null,
      creatorRole: null,
      merchantId: null,
    });
    setCustomDate([null, null]);
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
    setSelected([]);
    setSelectedMerchantIds([]);
  };

  const applyFilters = (sels) => {
    // Create a new object for mapped filter parameters
    const mappedFilterParams = {};

    filterDataWithMerchant.forEach((filter) => {
      const selectedValue = sels[filter.id];
      if (
        selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== "" &&
        !(Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        if (filter.id === "category") {
          mappedFilterParams[filter.id] = selectedValue;
        } else if (filter.id === "subcategory") {
          mappedFilterParams[filter.id] = selectedValue;
        } else if (filter.valueMap) {
          mappedFilterParams[filter.id] = filter.multiSelect
            ? selectedValue.map((val) => filter.valueMap[val])
            : filter.valueMap[selectedValue];
        } else {
          mappedFilterParams[filter.id] = selectedValue;
        }
      }
    });

    if (sels.merchantId) {
      mappedFilterParams.merchantName = sels.merchantName || "Merchant";
    }

    if (sels.duration === "CUSTOM") {
      if (customDate?.[0]) {
        mappedFilterParams.startDate = customDate[0].format("YYYY-MM-DD");
      }
      if (customDate?.[1]) {
        mappedFilterParams.endDate = customDate[1].format("YYYY-MM-DD");
      }
    }

    setFilterParams(mappedFilterParams);
    setCurrentPage(1);
  };

  // When applying filters from the Drawer
  const handleApply = () => {
    applyFilters(selections);
    setOpen(false);
  };

  const handleRemoveFilter = (chip) => {
    let newSelections = { ...selections };
    if (chip.id === 'subcategory') {
      newSelections.subcategory = selections.subcategory.filter(id => id !== chip.value);
    } else {
      newSelections[chip.id] = null;
      if (chip.id === 'merchantId') newSelections.merchantName = null;
      if (chip.id === 'category') newSelections.subcategory = [];
    }
    setSelections(newSelections);
    applyFilters(newSelections);
  };

  // Handle cancel button click
  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add("dropdown-open");
    } else {
      document.body.classList.remove("dropdown-open");
    }

    return () => {
      document.body.classList.remove("dropdown-open");
    };
  }, [open]);

  const items = [
    {
      title: "Offers",
    },
    {
      title: "Offers List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Title",
    },
    {
      name: "Merchant",
    },
    {
      name: "Category Type",
    },
    {
      name: "Offer Type",
    },
    {
      name: "Discount Type",
    },
    {
      name: "Status",
    },
    {
      name: "Created By",
    },
    {
      name: "Actions",
    },
  ];

  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  const normalizeOfferTypeLabel = (offerType) => {
    switch (offerType) {
      case "ONLINE_OFFER":
        return "online offer";
      case "INSTORE_SWOODLE_OFFER":
        return "instore swoodle offer";
      default:
        return String(offerType || "").toLowerCase();
    }
  };

  const normalizeDiscountTypeLabel = (discountType) => {
    switch (discountType) {
      case "FREE_OFFER":
        return "free offer";
      case "DISCOUNTED_PERCENT":
        return "discounted percent";
      case "FIXED_DOLLAR":
        return "fixed dollar";
      case "BUY_ONE_GET_ONE_FREE":
        return "buy one get one free";
      case "BUNDLE_OFFER":
        return "bundle offer";
      default:
        return String(discountType || "").toLowerCase();
    }
  };

  const matchesOfferSearch = (offer, query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return true;

    const searchValues = [
      offer?.title,
      offer?.merchant?.businessName,
      offer?.merchant?.business_name,
      offer?.category?.title,
      normalizeOfferTypeLabel(offer?.offerType),
      normalizeDiscountTypeLabel(offer?.offerDiscountType),
      normalizeOfferStatus(offer?.status),
      offer?.creatorRole === "admin"
        ? "admin"
        : offer?.creatorRole === "merchant"
          ? "merchant"
          : "",
    ];

    return searchValues.some((value) =>
      normalizeSearchText(value).includes(normalizedQuery)
    );
  };

  const getOffersData = useCallback(async (silent = false) => {

    if (!silent) setIsInitialLoading(true);

    const params = {
      limit: 500,
      category: filterParams.category || undefined,
      subcategory:
        filterParams.subcategory && filterParams.subcategory.length > 0
          ? filterParams.subcategory.join(",")
          : undefined,
      offerType: filterParams.offerType || undefined,
      status: filterParams.status || undefined,
      duration: filterParams.duration === "CUSTOM" ? undefined : (filterParams.duration || undefined),
      redemptionMethod: filterParams.redemptionMethod || undefined,
      merchantId: filterParams.merchantId || undefined,
      creatorRole: filterParams.creatorRole || undefined,
      silent: silent,
    };

    if (filterParams.duration === "CUSTOM" && customDate && (customDate[0] || customDate[1])) {
      params.startDate = customDate[0]?.format("YYYY-MM-DD");
      params.endDate = customDate[1]?.format("YYYY-MM-DD");
    }
    // Also include dates if they are already in filterParams (from localStorage)
    if (filterParams.startDate) params.startDate = filterParams.startDate;
    if (filterParams.endDate) params.endDate = filterParams.endDate;

    Object.keys(params).forEach(
      (key) => (params[key] === undefined ? delete params[key] : null)
    );
    try {
      const response = await dispatch(getNonDraftedOffers(params)).unwrap();
      if (response?.success) {
        setFullOffersList(response?.data || []);

        if (!hasLoaded) setHasLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setSearching(false);
      setIsInitialLoading(false);
    }
  }, [filterParams, customDate, dispatch, hasLoaded]);

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setDisplayedOffers(data.slice(startIndex, endIndex));
  };

  const normalizeOfferStatus = (status) => {
    const s = String(status || "").toLowerCase();

    if (["approved", "live"].includes(s)) return "live";
    if (["declined", "rejected"].includes(s)) return "rejected";
    if (["pause", "paused", "disabled"].includes(s)) return "disabled";
    if (["pending"].includes(s)) return "pending";
    if (["in-progress", "in progress", "in_process", "in process", "in_progress"].includes(s)) return "in-progress";
    if (s === "deleted") return "deleted";
    if (["draft", "drafted"].includes(s)) return "drafted";
    return s;
  };

  const filteredOffers = useMemo(() => {
    if (!Array.isArray(fullOffersList)) return [];
    return fullOffersList.filter((offer) => matchesOfferSearch(offer, searchQuery));
  }, [fullOffersList, searchQuery]);

  // 1. Initial/Filter/Search load
  useEffect(() => {
    // Only use global loader for initial load and explicit filter changes (excluding status refresh)
    const isSilent = deleteSuccess;
    
    // Add a tiny debounce to prevent handleReset from firing multiple times
    const handler = setTimeout(() => {
      getOffersData(isSilent ? true : false);
    }, 50);

    return () => clearTimeout(handler);
  }, [filterParams, deleteSuccess]);

  // 2. Logic to filter and slice data for display
  useEffect(() => {
    if (!filteredOffers) return;

    const nextTotalPages = Math.ceil(filteredOffers.length / 10) || 1;
    const nextPage = Math.min(currentPage, nextTotalPages);

    setCount(filteredOffers.length);
    setTotalPages(nextTotalPages);

    if (nextPage !== currentPage) {
      setCurrentPage(nextPage);
    }

    updatePageSlice(filteredOffers, nextPage);
  }, [filteredOffers, currentPage]);

  // Update your handleSearchChange to simply set the state
  const debouncedSearch = useMemo(() => {
    let timeoutId;
    return (value) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(value);
        setCurrentPage(1);
        setSearching(false);
      }, 500);
    };
  }, []);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    setSearching(true);
    debouncedSearch(value);
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of offer list when changing pages
    const container = document.getElementById('offer-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageIconClick = (offer) => {
    setClickedOffer(offer);
    setIsImageOpen(true);
  };

  const handleCloseImage = () => {
    setIsImageOpen(false);
    setClickedOffer(null);
  };

  const handleLocationClick = (offer) => {
    setClickedOffer(offer);
    setIsLocationOpen(true);
  };

  const handleDescriptionClick = (offer) => {
    setClickedOffer(offer);
    setIsDescriptionOpen(true);
  };

  const handleSubCategoryClick = (offer) => {
    setClickedOffer(offer);
    setIsSubCategoryOpen(true);
  };

  const handleRulesClick = (offer) => {
    setClickedOffer(offer);
    setIsRulesOpen(true);
  };

  const handleCloseLocation = () => {
    setIsLocationOpen(false);
    setClickedOffer(null);
  };

  const handleCloseDescription = () => {
    setIsDescriptionOpen(false);
    setClickedOffer(null);
  };

  const handleCloseSubCategory = () => {
    setIsSubCategoryOpen(false);
    setClickedOffer(null);
  };

  const handleCloseRules = () => {
    setIsRulesOpen(false);
    setClickedOffer(null);
  };

  const canEditOffer = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();
    const inProcessStatuses = ["in-process", "in progress", "in-progress", "in process"];
    const editableStatuses = ["drafted", "pending", "approved", "live"];
    return editableStatuses.includes(normalizedStatus) && !inProcessStatuses.includes(normalizedStatus);
  };

  const handleEditClick = (id, status, merchantId) => {
    if (!canEditOffer(status)) return;
    navigate("/admin/offer-form", {
      state: {
        id,
        ...(merchantId ? { merchantId } : {}),
      },
    });
  };

  const handleDeleteClick = (offer) => {
    setDeleteClicked(true);
    setClickedOffer(offer);
  };

  const handleKeepOffer = () => {
    setDeleteClicked(false);
    setClickedOffer(null);
  };

  const handleDeleteOffer = async () => {
    closePopup();
    setDeleteClicked(false);
    if (clickedOffer?._id !== null) {
      const deletePromise = await dispatch(
        deleteOffer({ id: clickedOffer?._id, merchantId: clickedOffer?.merchant?._id })
      )?.unwrap();
      if (deletePromise?.success === true) {
        setDeleteSuccess(!deleteSuccess);
        setClickedOffer(null);
      }
    }
  };

  const handleCloseInfo = () => {
    setInfoClicked(false);
    setClickedOffer(null);
  };

  const handleInfoClick = (offer) => {
    setClickedOffer(offer);
    setInfoClicked(true);
  };



  const fetchOfferDetails = async (offerId) => {
    try {
      const offerDetailsPromise = await dispatch(
        getOfferById({ id: offerId, silent: true })
      )?.unwrap();
      if (offerDetailsPromise?.success === true) {
        setOfferDetails(offerDetailsPromise?.data);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const syncOfferStatusInState = useCallback((offerId, nextStatus) => {
    if (!offerId || !nextStatus) return;

    setFullOffersList((prev) =>
      prev.map((offer) =>
        offer?._id === offerId ? { ...offer, status: nextStatus } : offer
      )
    );

    setDisplayedOffers((prev) =>
      prev.map((offer) =>
        offer?._id === offerId ? { ...offer, status: nextStatus } : offer
      )
    );

    setOfferDetails((prev) =>
      prev && prev._id === offerId ? { ...prev, status: nextStatus } : prev
    );
  }, []);

  const resolveOfferForPopup = (offerOrId) => {
    if (!offerOrId) return null;
    if (typeof offerOrId === "object") return offerOrId;

    return (
      (Array.isArray(fullOffersList) && fullOffersList.find((offer) => offer?._id === offerOrId)) ||
      null
    );
  };

  const openPopup = (offerOrId, openMode = null) => {
    const localOffer = resolveOfferForPopup(offerOrId);
    setOfferDetails(localOffer || null);
    setPopupOpenMode(openMode);
    setIsDetailOpen(true);

    if (!localOffer && offerOrId) {
      fetchOfferDetails(offerOrId);
    }
  };

  const closePopup = () => {
    setIsDetailOpen(false);
    setPopupOpenMode(null);
  };

  // Open direct from navigation state (e.g., notifications deep-link)
  useEffect(() => {
    const stateOfferId = location?.state?.openOfferId || location?.state?.offerId;
    if (stateOfferId) {
      openPopup(stateOfferId);
      // Clear state to avoid reopening on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state?.openOfferId, location?.state?.offerId, location.pathname, navigate]);

  // Handle initial search from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get("search");
    const merchantId = params.get("merchantId");

    let isModified = false;

    if (search && searchQuery !== search) {
      setSearchQuery(search);
      setSearchInput(search);
      isModified = true;
    }

    if (merchantId && filterParams.merchantId !== merchantId) {
      // Sync the filter panel's merchant selection too
      const updatedFilterParams = {
        ...filterParams,
        merchantId: merchantId,
        merchantName: search || filterParams.merchantName || "Selected Merchant",
      };
      setFilterParams(updatedFilterParams);

      // Also update the selection state so it shows correctly in the Filter Drawer if opened
      setSelections(prev => ({
        ...prev,
        merchantId: merchantId,
        merchantName: search || prev.merchantName || "Selected Merchant"
      }));

      isModified = true;
    }

    if (isModified) {
      setCurrentPage(1);
    }
  }, [location.search]);

  // Selectable offers on the current page only
  const getSelectableOffers = () =>
    (displayedOffers || []).filter(offer => isSelectable(offer.status));

  const handleSelectAll = () => {
    const selectableOffers = getSelectableOffers();
    const allSelected = selectableOffers.length > 0 && selectableOffers.every(o => selected.includes(o._id));

    let newSelected = [];
    if (allSelected) {
      const selectableIds = selectableOffers.map(o => o._id);
      newSelected = selected.filter(id => !selectableIds.includes(id));
    } else {
      newSelected = [...selected];
      selectableOffers.forEach(o => {
        if (!newSelected.includes(o._id)) {
          newSelected.push(o._id);
        }
      });
    }

    setSelected(newSelected);
    const remainingOffers = fullOffersList?.filter(o => newSelected.includes(o._id)) || [];
    setSelectedMerchantIds(remainingOffers.map(o => o?.merchant?._id).filter(Boolean));
  };

  const handleSelectOne = (event, id, status) => {
    if (!isSelectable(status)) return;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }

    setSelected(newSelected);
    const remainingOffers = fullOffersList?.filter(o => newSelected.includes(o._id)) || [];
    setSelectedMerchantIds(remainingOffers.map(o => o?.merchant?._id).filter(Boolean));
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Only Pending, Live, and rejected-style statuses are selectable for bulk actions
  const isSelectable = (status) => {
    const normalized = normalizeOfferStatus(status);
    return ["pending", "live", "disabled", "rejected"].includes(normalized);
  };

  const handleBulkApprove = () => {
    setShowApprovalConfirm(true);
  };

  const handleApproveConfirm = async () => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "approved",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList(prev => prev.map(o => selected.includes(o._id) ? { ...o, status: "approved" } : o));

        setStatusToggled(!statusToggled);
        setShowApprovalConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error approving offers:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleBulkDecline = () => {
    setShowDeclineConfirm(true);
  };

  // Move selected offers to pending
  const handleBulkPending = async () => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "pending",
        reason: "Moved to pending",
        // Provide email bodies to satisfy backend validation
        TextBody: "Your offer status has been changed to pending.",
        HtmlBody: "<p>Your offer status has been changed to <strong>pending</strong>.</p>",
        // Avoid showing warning toast if backend still returns the email-body message
        silent: true,
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList((prev) =>
          (prev || []).map((o) =>
            selected.includes(o._id) ? { ...o, status: "pending" } : o
          )
        );

        setStatusToggled(!statusToggled);
        setSelected([]);
        setSelectedMerchantIds([]);
        setShowActionModal(false);
        // Also re-fetch in the background to stay in sync
        try { getOffersData(); } catch { }
      }
    } catch (error) {
      console.error("Error setting pending offers:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleDeclineConfirm = async () => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "rejected",
        reason: "Bulk rejected",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList(prev => prev.map(o => selected.includes(o._id) ? { ...o, status: "rejected" } : o));

        setStatusToggled(!statusToggled);
        setShowDeclineConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error declining offers:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineConfirm(false);
  };
  const handleApproveCancel = () => {
    setShowApprovalConfirm(false);
  };

  const handleSingleApprovalConfirm = async () => {
    if (!singleApprovalOfferId || statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    try {
      const payload = {
        type: "offer",
        ids: [singleApprovalOfferId],
        status: "approved",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList(prev => prev.map(o => o._id === singleApprovalOfferId ? { ...o, status: "approved" } : o));

        setShowSingleApprovalConfirm(false);
        setSingleApprovalOfferId(null);
        setIsDetailOpen(false); // Close the details modal
        setStatusToggled(!statusToggled);
      }
    } catch (error) {
      console.error("Error approving offer:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleSingleApprovalCancel = () => {
    setShowSingleApprovalConfirm(false);
    setSingleApprovalOfferId(null);
  };

  const handleSingleApprovalClick = (offerId) => {
    setIsDetailOpen(false);
    setSingleApprovalOfferId(offerId);
    setShowSingleApprovalConfirm(true);
  };

  useEffect(() => {
    if (selected.length > 0) {
      setShowActionModal(true);
    } else {
      setShowActionModal(false);
    }
  }, [selected]);

  const shouldShowSelectionControls = activeFilterChips.length > 0;

  useEffect(() => {
    if (!shouldShowSelectionControls && (selected.length > 0 || selectedMerchantIds.length > 0)) {
      setSelected([]);
      setSelectedMerchantIds([]);
    }
  }, [shouldShowSelectionControls, selected, selectedMerchantIds]);

  // Calculate count of selectable items on current page for "Select All" checkbox
  const selectableOnPage = getSelectableOffers();
  const selectableCount = selectableOnPage.length;
  const allSelectableSelected =
    selectableCount > 0 && selectableOnPage.every(o => selected.includes(o._id));

  // Determine which bulk action buttons to show based on selected statuses
  const selectedStatuses = selected
    .map((id) => fullOffersList?.find((o) => o._id === id)?.status)
    .filter(Boolean)
    .map((s) => normalizeOfferStatus(s));
  const hasPendingSelected = selectedStatuses.includes("pending");
  const showPendingOnly = selected.length > 0 && !hasPendingSelected; // all selected are approved/rejected-style

  console.log("offerDetails", offerDetails);
  const handleApproveClickDirect = async (offerId) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    closePopup();
    try {
      // Get the offer details to access merchant ID
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "approved",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: "",
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList(prev => prev.map(o => o._id === offerId ? { ...o, status: "approved" } : o));

        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
      }
    } catch (error) {
      console.error("Error approving offer:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleDeclineClick = async (offerId, reason) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    closePopup();
    try {
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "rejected",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: reason || `Changed from ${offer.status} to rejected`,
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        // Optimistic Status Update (backend controls ordering)
        setFullOffersList(prev => prev.map(o => o._id === offerId ? { ...o, status: "rejected" } : o));

        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
      }
    } catch (error) {
      console.error("Error declining offer:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  // Handle status click to change to pending
  const handleStatusClick = async (offerId, newStatus, declineReason) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);
    let previousStatus = null;
    try {
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }
      previousStatus = offer.status;
      const statusLabel = String(newStatus || "").trim();
      const prettyStatusLabel = statusLabel
        ? statusLabel
            .split(/[\s_-]+/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ")
        : "Pending";

      syncOfferStatusInState(offerId, newStatus);

      const payload = {
        type: "offer",
        status: newStatus,
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason:
          newStatus === "rejected"
            ? (declineReason || `Changed from ${offer.status} to rejected`)
            : `Changed from ${offer.status} to ${newStatus}`,
        ...(newStatus === "pending"
          ? {
            TextBody: `Your offer "${offer?.title || ""}" has been moved to ${prettyStatusLabel} for review.`,
            HtmlBody: `<p>Your offer <strong>${offer?.title || ""}</strong> has been moved to <strong>${prettyStatusLabel}</strong> for review.</p>`,
          }
          : {}),
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
      } else {
        syncOfferStatusInState(offerId, previousStatus);
      }
    } catch (error) {
      if (previousStatus) {
        syncOfferStatusInState(offerId, previousStatus);
      }
      console.error("Error changing offer status:", error);
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  return (
    <>
      {(loading || isInitialLoading) && <Loader />}
      {clickedOffer?.thumbnailImage !== null && isImageOpen === true && (
        <ImagePopup
          image={getS3PublicUrl(clickedOffer?.thumbnailImage)}
          handleCloseImage={handleCloseImage}
        />
      )}
      {isLocationOpen === true && (
        <LocationPopup
          locations={clickedOffer?.tempLocations}
          handleCloseLocation={handleCloseLocation}
        />
      )}
      {clickedOffer?.description !== "" && isDescriptionOpen === true && (
        <DetailsPopup
          content={clickedOffer?.description}
          handleClose={handleCloseDescription}
          icon={descriptionIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      {clickedOffer?.subCategory?.length > 0 && isSubCategoryOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.subCategory}
          handleCloseSubCategory={handleCloseSubCategory}
          icon={<SubCategoryIcon className="w-[10%]" />}
          heading="Subcategories"
        />
      )}
      {clickedOffer?.ruleAndRegulation?.length > 0 && isRulesOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.ruleAndRegulation}
          handleCloseSubCategory={handleCloseRules}
          icon={<RulesIcon className="w-[10%]" />}
          heading="Rules and Regulations"
        />
      )}

      {/* Updated PopupContent with dynamic redemption codes */}
      {isDetailOpen && (
        <div className="popup-container details-modal-backdrop details-modal-backdrop-enter">
          <div className="details-modal-panel details-modal-panel-enter">
            <PopupContent
              offerDetails={offerDetails}
              handleDeleteClick={handleDeleteClick}
              handleEditClick={handleEditClick}
              handleSingleApprovalClick={handleApproveClickDirect}
              handleDeclineClick={handleDeclineClick}
              handleStatusClick={handleStatusClick}
              onDeclineRequest={(offerId) => {
                openPopup(offerId, "decline");
              }}
              onStatusChange={(offerId, nextStatus) => {
                syncOfferStatusInState(offerId, nextStatus);
              }}
              onClose={closePopup}
              onDuplicated={getOffersData}
              startRejectionOpen={popupOpenMode === "decline"}
            />
          </div>
        </div>
      )}

      {showApprovalConfirm && (
        <CancelPopup
          handleNo={handleApproveCancel}
          handleYes={handleApproveConfirm}
          heading="Approve Offers"
          content="Do you really want to approve all selected offers?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showDeclineConfirm && (
        <CancelPopup
          handleNo={handleDeclineCancel}
          handleYes={handleDeclineConfirm}
          heading="Reject Offers"
          content="Do you really want to reject all selected offers?"
          confirmation={false}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showSingleApprovalConfirm && (
        <CancelPopup
          handleNo={handleSingleApprovalCancel}
          handleYes={handleSingleApprovalConfirm}
          heading="Approve Offer"
          content="Do you really want to approve this offer?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}

      <div className="flex flex-col sm:flex-row justify-between col-span-12 gap-4">
        <BreadCrumsHeader heading="Offers List" items={items} />
      </div>
      {deleteClicked === true ? (
        <CancelPopup
          handleNo={handleKeepOffer}
          handleYes={handleDeleteOffer}
          heading={"Delete Offer"}
          content={"Are you sure you want to delete this offer?"}
          image={deleteImg}
        />
      ) : null}
      {infoClicked === true && (
        <DetailsPopup
          content={clickedOffer?.reason}
          handleClose={handleCloseInfo}
          icon={infoIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      <div className="w-full flex justify-between items-center mt-6 gap-3">
        <SearchBar
          parentDivClass="w-full sm:w-[60%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none rounded-[5px] border border-[#D9D9D9] focus:border-[#00ADEF]"
          iconClassName="absolute right-[10px] top-1/2 -translate-y-1/2 w-[22px] h-[22px]"
          placeholder="Search"
          value={searchInput}
          handleChange={(e) => handleSearchChange(e.target.value)}
          onClear={() => handleSearchChange("")}
          loading={searching}
        />
        <div onClick={() => setOpen(true)} className="cursor-pointer">
          <FilterButton isOpen={open} activeCount={activeFilterChips.length} />
        </div>
        <MuiDrawer
          anchor="right"
          open={open}
          onClose={handleCancel}
          ModalProps={{ keepMounted: true }}
          TransitionComponent={Slide}
          SlideProps={{
            appear: true,
            timeout: { enter: 700, exit: 500 },
            easing: {
              enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: '400px' },
              padding: '0',
              height: '100%'
            }
          }}
        >
          <FilterPanel
            filterData={filterDataWithMerchant}
            selections={selections}
            appliedSelections={filterParams}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleReset={handleReset}
            handleSelectionChange={handleSelectionChange}
            applyDisabled={isOfferCustomDateIncomplete}
            style={{ maxHeight: "unset" }}
          />
        </MuiDrawer>
      </div>      <ActiveFilterBar
        chips={activeFilterChips}
        onRemove={handleRemoveFilter}
        onClearAll={handleReset}
      />
      <div id="offer-list-container" className="col-span-12 mt-4 relative min-h-[400px]">
        <>
          {/* Desktop Table */}
          <TableContainer style={{ width: "100%", overflowX: "auto" }} className="hidden md:block">
            <Table
              sx={{
                minWidth: "800px",
                borderCollapse: "separate",
                borderSpacing: "0px 8px",
              }}
              aria-label="simple table"
            >
              <TableHead className="bg-[#00ADEF]">
                <TableRow
                  className="h-[50px]"
                  sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
                >
                  {shouldShowSelectionControls && (
                    <TableCell className="!border-b-0">
                      {selectableCount > 0 && (
                        <Checkbox
                          checked={allSelectableSelected}
                          onChecked={handleSelectAll}
                          divClassName="flex items-center"
                          UncheckedIcon={UncheckedIcon}
                          CheckedIcon={CheckedWhiteIcon}
                          checkClassName={"!w-[22px] !h-[22px]"}
                        />
                      )}
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    return (
                      <TruncatedTableCell
                        key={column.name}
                        className="!text-[#FFFFFF]"
                        textClassName="!text-[#FFFFFF] !font-[700]"
                        title={column.name}
                        maxChars={14}
                      >
                        {column.name}
                      </TruncatedTableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedOffers && displayedOffers.length > 0 &&
                  displayedOffers.map((offer, index) => {
                    const isItemSelected = isSelected(offer._id);
                    const itemSelectable = isSelectable(offer.status);
                    return (
                      <TableRow
                        className="h-[50px]"
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor:
                            (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                        }}
                        key={offer._id}
                      >
                        {shouldShowSelectionControls && (
                          <TableCell>
                            {itemSelectable && (
                              <Checkbox
                                checked={isItemSelected}
                                onChecked={(event) =>
                                  handleSelectOne(event, offer._id, offer.status)
                                }
                                divClassName="flex items-center"
                                checkClassName="!w-[22px] !h-[22px]"
                              />
                            )}
                          </TableCell>
                        )}
                        <TruncatedTableCell className="max-w-[50px]">{(currentPage - 1) * 10 + index + 1}</TruncatedTableCell>
                        <TruncatedTableCell className="max-w-[100px]" title={offer?.title}>
                          {offer?.title || "N/A"}
                        </TruncatedTableCell>
                        <TruncatedTableCell className="max-w-[120px]" title={offer?.merchant?.businessName || offer?.merchant?.business_name}>
                          {offer?.merchant?.businessName || offer?.merchant?.business_name || "N/A"}
                        </TruncatedTableCell>
                        <TruncatedTableCell className="max-w-[80px]" title={offer?.category?.title}>
                          {offer?.category?.title || "N/A"}
                        </TruncatedTableCell>
                        <TruncatedTableCell className="max-w-[180px]">
                          {(() => {
                            switch (offer?.offerType) {
                              case "ONLINE_OFFER":
                                return "ONLINE OFFER";
                              case "INSTORE_SWOODLE_OFFER":
                                return "INSTORE SWOODLE OFFER";
                              default:
                                return "N/A";
                            }
                          })()}
                        </TruncatedTableCell>
                        <TruncatedTableCell className="max-w-[80px]" title={offer?.offerDiscountType}>
                          {(() => {
                            switch (offer?.offerDiscountType) {
                              case "FREE_OFFER":
                                return "FREE OFFER";
                              case "DISCOUNTED_PERCENT":
                                return "DISCOUNTED PERCENT";
                              case "FIXED_DOLLAR":
                                return "FIXED DOLLAR";
                              case "BUY_ONE_GET_ONE_FREE":
                                return "BUY ONE GET ONE FREE";
                              case "BUNDLE_OFFER":
                                return "BUNDLE OFFER";
                              default:
                                return "N/A";
                            }
                          })()}
                        </TruncatedTableCell>
                        <TableCell
                          className="!px-2 whitespace-nowrap"
                          style={{ width: "1%", minWidth: "120px", overflow: "visible" }}
                        >
                          <Confirmation
                            status={offer?.status}
                            onStatusClick={handleStatusClick}
                            offerId={offer?._id}
                            enablePendingAction={true}
                            handleInfoClick={() => handleInfoClick(offer)}
                            creatorRole={offer?.creatorRole}
                            offerDetails={offer}
                            onDeclineRequest={(declineOfferId) => {
                              openPopup(declineOfferId || offer?._id, "decline");
                            }}
                          />
                        </TableCell>
                        <TruncatedTableCell className="max-w-[100px]">
                          {offer?.creatorRole === "admin" ? "Admin" : (offer?.creatorRole === "merchant" ? "Merchant" : "N/A")}
                        </TruncatedTableCell>
                        <TableCell className="max-w-[150px] text-center">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-1 border border-[#00ADEF] rounded-[6px] px-4 h-[36px] min-w-[116px] whitespace-nowrap mx-auto transition-colors hover:bg-[#F0FBFF]"
                            onClick={() => openPopup(offer?._id)}
                          >
                            <span className="font-[600] text-[13px] font-poppins leading-none text-[#00ADEF] whitespace-nowrap">
                              View Details
                            </span>
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {!loading && !searching && hasLoaded && displayedOffers && displayedOffers.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (shouldShowSelectionControls ? 1 : 0)}
                      align="center"
                      className="!border-b-0 !p-0"
                    >
                      <TableEmptyState itemName="Offer" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile View */}
          <div className="md:hidden flex flex-col gap-4">
            {displayedOffers && displayedOffers.length > 0 &&
              displayedOffers.map((offer) => (
                <div
                  key={offer._id}
                  className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start text-xs">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[14px] font-[600] text-[#303030] truncate pr-2" title={offer?.title}>
                        {offer?.title || "N/A"}
                      </span>
                      <span className="text-[12px] text-[#767676] truncate pr-2">
                        {offer?.category?.title || "N/A"}
                      </span>
                    </div>
                    <div className="flex-shrink-0">
                      <Confirmation
                        status={offer?.status}
                        onStatusClick={handleStatusClick}
                        offerId={offer?._id}
                        enablePendingAction={true}
                        handleInfoClick={() => handleInfoClick(offer)}
                        creatorRole={offer?.creatorRole}
                        offerDetails={offer}
                        onDeclineRequest={(declineOfferId) => {
                          openPopup(declineOfferId || offer?._id, "decline");
                        }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[13px] font-poppins text-[#303030]">
                    <div className="flex flex-col">
                      <span className="text-[#8C8C8C] text-[11px]">Offer Type</span>
                      <span className="truncate">
                        {offer?.offerType === "ONLINE_OFFER" ? "Online" : "In-store"}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[#8C8C8C] text-[11px]">Created By</span>
                      <span className="truncate">
                        {offer?.creatorRole === "admin" ? "Admin" : (offer?.merchant?.businessName || "Merchant")}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="w-full mt-2 inline-flex justify-center items-center border border-[#00ADEF] rounded-[6px] py-2 px-4 bg-[#F0FBFF] hover:bg-[#E0F7FA] transition-colors whitespace-nowrap"
                    onClick={() => openPopup(offer?._id)}
                  >
                    <span className="font-[600] text-[13px] text-[#00ADEF] font-poppins leading-none whitespace-nowrap">
                      View Details
                    </span>
                  </button>
                </div>
              ))
            }
            {!loading && !searching && hasLoaded && (!displayedOffers || displayedOffers.length === 0) && (
              <TableEmptyState itemName="Offer" />
            )}
          </div>

          {displayedOffers && displayedOffers.length > 0 && (
            <Pagination
              totalPages={totalPages}
              handlePageChange={handlePageChange}
              currentPage={currentPage}
            />
          )}

          {/* Action Modal */}
          {
            shouldShowSelectionControls && showActionModal && (
              <div
                className="fixed bottom-16 left-[57%] transform -translate-x-1/2 bg-white rounded-[6px] px-4 h-[60px] flex items-center gap-4 z-50 text-[14px] font-[600] text-[#303030] border border-[#0000001A]"
                style={{ boxShadow: "0px 4px 20px 0px #00000029" }}
              >
                <div className="flex items-center gap-2">
                  <span className="!text-[12px] !font-[500] !text-[#000000] border border-[#2A35484D] rounded-[4px] w-[24px] h-[24px] flex items-center justify-center">
                    {selected.length}
                  </span>
                  <span className="!font-[500]">Selected</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allSelectableSelected}
                        onChecked={handleSelectAll}
                        divClassName="flex items-center"
                        UncheckedIcon={UncheckedGreyIcon}
                        checkClassName={"!w-[22px] !h-[22px]"}
                      />
                      <span className="whitespace-nowrap !font-[500]">
                        Select All
                      </span>
                    </div>
                    {showPendingOnly ? (
                      <button
                        onClick={handleBulkPending}
                        className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap px-4 py-1 rounded-[6px]"
                      >
                        Pending Offers
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleBulkApprove}
                          className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap pl-4 pr-8 py-1 rounded-[6px] flex items-center gap-2"
                        >
                          <img
                            src={whiteTick}
                            alt="tick"
                            className="h-[16px] w-[16px]"
                          />
                          Approve Offers
                        </button>
                        <button
                          onClick={handleBulkDecline}
                          className="border border-[#00ADEF] text-[#00ADEF] h-[38px] px-4 py-1 rounded-[6px] whitespace-nowrap"
                        >
                          Reject Offers
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSelected([]);
                      setSelectedMerchantIds([]);
                    }}
                    className="w-[24px] h-[24px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                    title="Clear selection"
                  >
                    <img src={blueCross} alt="Close" className="w-[19px] h-[19px]" />
                  </button>
                </div>
              </div>
            )
          }
        </>
      </div>
    </>
  );
};

export default AdminOfferlist;
